import { cva } from "cva";
import { X } from "lucide-react";
import type {
  TagGroupProps,
  TagListProps,
  TagProps,
} from "react-aria-components";
import {
  Button,
  Label,
  Tag,
  TagGroup,
  TagList,
  Text,
} from "react-aria-components";
interface MyTagGroupProps<T>
  extends Omit<TagGroupProps, "children">,
    Pick<TagListProps<T>, "items" | "children" | "renderEmptyState"> {
  label?: string;
  description?: string;
  errorMessage?: string;
  className?: string;
}
const tagGroupStyles = cva({
  base: "flex flex-col text-sm transition-colors duration-300 ease-in-out text-theme-outline",
});
const tagListStyles = cva({ base: "flex flex-wrap " });
const tagStyles = cva({
  base: "inline-flex items-center w-[144px] justify-center py-2 text-sm first:rounded-l-md last:rounded-r-md last:border-l-0 border first:border-r-theme-gradient-from/40 cursor-default outline-none transition-colors duration-300 ease-in-out",
  variants: {
    isHovered: {
      true: "bg-theme-gradient-from/10 border-theme-gradient-from/10  transition-colors duration-200 ease-in-out cursor-pointer",
    },
    isFocusVisible: { true: "outline-2 outline-blue-500 outline-offset-2" },
    isSelected: {
      true: "bg-theme-gradient-from/30 border-theme-gradient-from/40 text-theme-on-glass transition-colors duration-300 ease-in-out",
    },
  },
});
const removeButtonStyles = cva({
  base: "flex items-center justify-center p-0 bg-transparent border-none cursor-pointer opacity-70 hover:opacity-100 transition-opacity duration-200",
});
export function TagGroupBox<T extends object>({
  label,
  description,
  errorMessage,
  items,
  children,
  renderEmptyState,
  className,
  ...props
}: MyTagGroupProps<T>) {
  return (
    <TagGroup
      selectionBehavior="toggle"
      className={tagGroupStyles({ className })}
      disallowEmptySelection={true}
      {...props}
    >
      <Label>{label}</Label>
      <TagList
        className={tagListStyles()}
        items={items}
        renderEmptyState={renderEmptyState}
      >
        {children}
      </TagList>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <Text slot="errorMessage">{errorMessage}</Text>}
    </TagGroup>
  );
}
export function TagItem({
  children,
  className,
  ...props
}: Omit<TagProps, "children"> & {
  children?: React.ReactNode;
  className?: string;
}) {
  const textValue = typeof children === "string" ? children : undefined;
  return (
    <Tag
      textValue={textValue}
      className={({ isHovered, isFocusVisible, isSelected }) =>
        tagStyles({ isHovered, isFocusVisible, isSelected, className })
      }
      {...props}
    >
      {({ allowsRemoving }) => (
        <>
          {children}
          {allowsRemoving && (
            <Button slot="remove" className={removeButtonStyles()}>
              <X size={12} />{" "}
            </Button>
          )}
        </>
      )}
    </Tag>
  );
}
